import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import type { ClientAccessDto, OAuthClientDto } from "@platform/core";
import { Alert, Badge, Button, DataGrid, Dialog, EmptyState, useToast, type GridColDef } from "@platform/ui";
import { isApiError } from "../../api/errors";
import { useApi, useConsole } from "../../context";
import { useT } from "../../i18n";
import { CopyButton } from "../../lib/copy";
import { errorText } from "../../lib/errors";
import { formatDate } from "../../lib/format";
import { useGridText } from "../../lib/grid";
import { ErrorBanner, PageCard, ReloadButton } from "../../layout/Page";
import { ClientForm, EMPTY_CLIENT, cleanUris, toScopes, type ClientFormValue } from "./ClientForm";
import { SecretDialog } from "./SecretDialog";
import { AccessEditor, NEW_CLIENT_ACCESS, accessProblem, cleanAccess } from "./AccessEditor";
import { accessSummary, useGroups, useUserTypes } from "../../lib/access";

export function ClientsPage() {
  const { t, locale } = useT();
  const api = useApi();
  const { can, config } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const clients = useQuery({ queryKey: ["clients"], queryFn: api.clients.list });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<ClientFormValue>(EMPTY_CLIENT);
  const [access, setAccess] = useState<ClientAccessDto>(NEW_CLIENT_ACCESS);
  const [secret, setSecret] = useState<{ clientId: string; secret: string } | null>(null);
  const types = useUserTypes(open);
  const groups = useGroups(open);
  const register = useMutation({
    mutationFn: () =>
      api.clients.register({
        name: form.name.trim(),
        type: form.type,
        redirectUris: cleanUris(form.redirectUris),
        postLogoutRedirectUris: cleanUris(form.postLogoutRedirectUris),
        allowedScopes: toScopes(form.allowedScopes),
        firstParty: form.firstParty,
        ...(form.backchannelLogoutUri.trim()
          ? { backchannelLogoutUri: form.backchannelLogoutUri.trim() }
          : {}),
        ...(form.ownerTeam.trim() ? { ownerTeam: form.ownerTeam.trim() } : {}),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        access: cleanAccess(access),
      }),
    onSuccess: (r) => {
      toast({ title: t("clients.registered"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["clients"] });
      setOpen(false);
      setForm(EMPTY_CLIENT);
      setAccess(NEW_CLIENT_ACCESS);
      if (r.clientSecret) setSecret({ clientId: r.client.clientId, secret: r.clientSecret });
    },
  });
  const fieldErrors = isApiError(register.error) ? register.error.fieldErrors() : undefined;
  const text = useGridText();
  const columns = useMemo<GridColDef<OAuthClientDto>[]>(
    () => [
      {
        field: "name",
        headerName: t("clients.col.name"),
        minWidth: 200,
        hideable: false,
        renderCell: ({ row: c }) => (
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium">{c.name}</span>
            {c.firstParty && <Badge tone="info">{t("clients.firstParty")}</Badge>}
          </span>
        ),
      },
      {
        field: "clientId",
        headerName: t("clients.col.clientId"),
        minWidth: 240,
        renderCell: ({ row: c }) => (
          <span className="inline-flex min-w-0 items-center gap-1">
            <code className="truncate font-mono text-xs">{c.clientId}</code>
            <CopyButton value={c.clientId} />
          </span>
        ),
      },
      {
        field: "type",
        headerName: t("clients.col.type"),
        type: "singleSelect",
        width: 170,
        valueOptions: [
          { value: "public", label: t("clients.type.public") },
          { value: "confidential", label: t("clients.type.confidential") },
        ],
      },
      {
        field: "firstParty",
        headerName: t("clients.firstParty"),
        type: "boolean",
        width: 120,
        hiddenByDefault: true,
        valueFormatter: (v) => (v ? "✓" : ""),
      },
      {
        field: "ownerTeam",
        headerName: t("clients.ownerTeam"),
        width: 160,
        hiddenByDefault: true,
      },
      {
        field: "access",
        headerName: t("clients.col.access"),
        minWidth: 180,
        sortable: false,
        valueGetter: (c) => accessSummary(c.access, t),
        renderCell: ({ row: c }) =>
          c.access.mode === "all" ? (
            <Badge tone="warning">{t("access.everyone")}</Badge>
          ) : (
            <span className="truncate text-sm">{accessSummary(c.access, t)}</span>
          ),
      },
      {
        field: "createdAt",
        headerName: t("users.col.created"),
        type: "dateTime",
        width: 140,
        valueFormatter: (v) => formatDate(String(v), locale, config.timeZone),
      },
      {
        field: "disabled",
        headerName: t("clients.col.status"),
        type: "singleSelect",
        width: 140,
        valueGetter: (c) => (c.disabled ? "disabled" : "enabled"),
        valueOptions: [
          { value: "enabled", label: t("common.enabled") },
          { value: "disabled", label: t("common.disabled") },
        ],
        renderCell: ({ row: c }) => (
          <Badge dot tone={c.disabled ? "danger" : "success"}>
            {c.disabled ? t("common.disabled") : t("common.enabled")}
          </Badge>
        ),
      },
    ],
    [t, locale, config.timeZone],
  );
  return (
    <PageCard>
      {clients.isError && <ErrorBanner error={clients.error} onRetry={() => void clients.refetch()} />}
      <DataGrid
        id="oauth-clients"
        label={t("nav.oauthClients")}
        localeText={text}
        rows={clients.data?.items ?? []}
        columns={columns}
        getRowId={(c) => c.clientId}
        loading={clients.isFetching}
        onRowClick={(c) =>
          void navigate({ to: "/oauth-clients/$clientId", params: { clientId: c.clientId } })
        }
        getRowAttributes={(c) => ({ "data-testid": "client-row", "data-client-id": c.clientId })}
        noRowsContent={<EmptyState title={t("clients.empty")} />}
        toolbarActions={
          <>
            <ReloadButton onClick={() => void clients.refetch()} />
            {can("clients:write") && (
              <Button data-testid="register-client-button" onClick={() => setOpen(true)}>
                <Plus className="size-4" aria-hidden />
                {t("clients.register")}
              </Button>
            )}
          </>
        }
      />
      <Dialog
        open={open}
        onOpenChange={setOpen}
        size="lg"
        title={t("clients.register")}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              data-testid="submit-client-button"
              loading={register.isPending}
              disabled={
                !form.name.trim() ||
                !cleanUris(form.redirectUris).length ||
                accessProblem(access, types.data?.items ?? []) !== null
              }
              onClick={() => register.mutate()}
            >
              {t("clients.register")}
            </Button>
          </>
        }
      >
        {register.isError && (
          <Alert tone="error" className="mb-4">
            {errorText(register.error)}
          </Alert>
        )}
        <ClientForm value={form} onChange={setForm} isNew {...(fieldErrors ? { fieldErrors } : {})} />
        <section className="mt-2 border-t border-line pt-4" aria-labelledby="register-access-title">
          <h3 id="register-access-title" className="mb-1 text-sm font-semibold">
            {t("clients.tab.access")}
          </h3>
          <p className="mb-3 text-xs text-ink-2">{t("access.newDefault")}</p>
          <AccessEditor
            value={access}
            onChange={setAccess}
            types={types.data?.items}
            groups={groups.data?.items}
          />
        </section>
      </Dialog>
      <SecretDialog
        clientId={secret?.clientId ?? ""}
        secret={secret?.secret ?? null}
        onClose={() => setSecret(null)}
      />
    </PageCard>
  );
}
